import React, { useCallback, useMemo, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { Button, Flex, message, Modal, Steps, Tooltip } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { RcFile } from 'antd/lib/upload';
import { FormInstance } from 'antd/lib/form';

import {
  GiidePublishStateObj,
  ImageEditorValidation,
  PreviewDetails,
  SettingsData,
} from '../../types';
import { IState, SocialUrls } from '../../graphql/types';
import {
  decodeSocialUrls,
  encodeSocialUrls,
  getGiideSettingsTooltipMessage,
  handleImageInEditor,
  settingsTabArray as stepArray,
  SettingsTabKeys as StepKeys,
  SettingsTabText as StepText,
} from '../utils';
import {
  setImageEditorState,
  setPreviewDetails,
  setPublishActiveStep,
  setPublishFlowGiideBeforePreview,
  updateGiideSettings,
} from '../../actions';

import ImageSettings from '../GiideSettings/ImageSettings';
import DetailsSettings from '../GiideSettings/DetailsSettings';
import styles from './Publish.module.css';
import GiidePreview from '../GiideWithStep/GiidePreview';
import usePreview from '../../hooks/usePreview';
import AvatarEditor from 'react-avatar-editor';
import useMutation from '../../hooks/useMutation';
import {
  CardDetailsInput,
  UpdateGiideInput,
  UpdateGiideMutation,
} from '../../graphql/api';
import gql from 'graphql-tag';

const { Step } = Steps;

const updateGiideMutation = gql`
  mutation UpdateGiide($input: UpdateGiideInput!) {
    updateGiide(input: $input) {
      id
      cardDetails {
        useCustomThumbnail
        thumbnailImageService
      }
      noindex
    }
  }
`;

interface IProps {
  giideData: SettingsData;
  publishing: GiidePublishStateObj;
  detailsForm: FormInstance;
  imageForm: FormInstance;
  uploadAvatarProgress: number;
  avatar: string | null;
  // invites: UserInvite[] | null;
  closeModal: () => void;
  updateInput: (key: string, value: string | boolean) => void;
  updateColor: (cardColor: string) => void;
  updateShade: (cardShade: string) => void;
  updateThumbnail: (thumbnail: string) => void;
  updateThumbnailData: (file: File) => void;
  uploadThumbnail: (
    file,
    returnData?: boolean
  ) => Promise<boolean> | Promise<{}>;
  removeThumbnail: () => void;
  selectCustomThumbnail: () => void;
  updateSpeakerInput: (key: string, value: string) => void;
  updateSpeakerUrlInput: (key: string, vlaue: string) => void;
  updateSpeakerName: (fullName: string) => void;
  handleAvatarUpload: (file: RcFile) => Promise<void>;
  removeAvatar: () => void;
  saveAndPublish: (e: React.MouseEvent<HTMLButtonElement>) => Promise<void>;
  // addInvite: (email: string) => void;
  // removeInvite: (email: string) => void;
  setGiideData: (giide: SettingsData) => void;
  updateGiideTitleName: (newTitle: string) => void;
  open: boolean;
}

const PublishFlow: React.FC<IProps> = ({
  giideData,
  publishing,
  detailsForm,
  imageForm,
  uploadAvatarProgress,
  avatar,
  // invites,
  closeModal,
  updateInput,
  updateColor,
  updateShade,
  updateThumbnail,
  updateThumbnailData,
  uploadThumbnail,
  removeThumbnail,
  selectCustomThumbnail,
  updateSpeakerInput,
  updateSpeakerUrlInput,
  updateSpeakerName,
  handleAvatarUpload,
  removeAvatar,
  saveAndPublish,
  // addInvite,
  // removeInvite,
  setGiideData,
  updateGiideTitleName,
  open,
}) => {
  const dispatch = useDispatch();
  const activeStep = useSelector<IState, number>(
    state => state.publishOpenedStep
  );
  const [editorRef, setEditorRef] = useState<AvatarEditor | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [noIndexLoading, setIndexLoading] = useState<boolean>(false);
  const { loadPreview } = usePreview();
  const previewDetails = useSelector<IState, PreviewDetails>(
    state => state.previewDetails ?? { loaded: false, loading: false }
  );
  const useTemplate = useSelector<IState, boolean>(
    state =>
      state.useTemplate ??
      !!(
        !state.giide?.cardDetails?.useCustomThumbnail &&
        state.giide?.cardDetails?.thumbnailImg
      )
  );
  const publishFlowGiideBeforePreview = useSelector<
    IState,
    SettingsData | undefined
  >(state => state.publishFlowGiideBeforePreview);
  const imageEditorValidation = useSelector<IState, ImageEditorValidation>(
    state => state.imageEditorValidation ?? { failed: false }
  );
  const imageMimeTypeLoaded = useSelector<IState, string>(
    state => state.imageMimeTypeLoaded ?? ''
  );

  const { callMutation } = useMutation<UpdateGiideInput, UpdateGiideMutation>(
    updateGiideMutation
  );

  const tooltipMessage = useMemo<string | undefined>(() => {
    return getGiideSettingsTooltipMessage(
      stepArray[activeStep],
      useTemplate,
      giideData,
      imageEditorValidation
    );
  }, [giideData, activeStep, useTemplate, imageEditorValidation]);

  const onClickBack = useCallback(() => {
    if (
      stepArray[activeStep] === StepKeys.preview &&
      publishFlowGiideBeforePreview
    ) {
      const giideSettingsInput = {
        ...publishFlowGiideBeforePreview,
        cardDetails: {
          ...publishFlowGiideBeforePreview.cardDetails,
          thumbnailCustomPath: useTemplate
            ? ''
            : giideData.cardDetails.thumbnailCustomPath,
        },
      };
      dispatch(updateGiideSettings(giideSettingsInput));
    }
    if (stepArray[activeStep] === StepKeys.image && editorRef) {
      dispatch(
        setImageEditorState({
          position: { x: editorRef.state.image.x, y: editorRef.state.image.y },
          scale: editorRef.props.scale,
        })
      );
    }

    if (activeStep > 0) {
      dispatch(setPublishActiveStep(activeStep - 1));
    }
  }, [
    activeStep,
    dispatch,
    editorRef,
    publishFlowGiideBeforePreview,
    giideData.cardDetails.thumbnailCustomPath,
    useTemplate,
  ]);

  const onClickNext = useCallback(async () => {
    setLoading(true);
    let proceed = true;

    if (stepArray[activeStep] === StepKeys.image) {
      const uploadData: CardDetailsInput = await handleImageInEditor(
        editorRef,
        imageMimeTypeLoaded,
        dispatch,
        uploadThumbnail
      );
      dispatch(setPublishFlowGiideBeforePreview(giideData));

      const encodedSocialUrls = encodeSocialUrls(
        giideData.speaker.socialUrls as SocialUrls
      );
      const giideDataInput = {
        ...giideData,
        speaker: {
          ...giideData.speaker,
          socialUrls: encodedSocialUrls,
        },
      };

      if (uploadData) {
        giideDataInput.cardDetails = uploadData;
      } else {
        giideDataInput.cardDetails = {
          ...giideData.cardDetails,
          thumbnailCustomName: useTemplate
            ? ''
            : giideData.cardDetails.thumbnailCustomName,
          thumbnailCustomPath: useTemplate
            ? ''
            : giideData.cardDetails.thumbnailCustomPath,
          useCustomThumbnail: !useTemplate,
          thumbnailImageService: useTemplate
            ? undefined
            : giideData.cardDetails.thumbnailImageService,
        };
      }

      if (uploadData || uploadData === undefined) {
        const updatedGiide = await callMutation(giideDataInput);

        if (updatedGiide.data.updateGiide) {
          giideDataInput.speaker.socialUrls = decodeSocialUrls(
            giideData.speaker.socialUrls as SocialUrls
          );
          batch(async () => {
            dispatch(updateGiideSettings(giideDataInput));
            dispatch(setPreviewDetails({ loaded: false, loading: true }));
            const previewLoaded = await loadPreview(false);
            dispatch(
              setPreviewDetails({ loaded: previewLoaded, loading: false })
            );
          });
        } else {
          proceed = false;
        }
      } else {
        proceed = false;
      }
    } else if (stepArray[activeStep] === StepKeys.details) {
      const updatedGiide = await callMutation(giideData);
      if (updatedGiide.data.updateGiide) {
        dispatch(updateGiideSettings(giideData));
      } else {
        proceed = false;
      }
    }

    if (activeStep < stepArray.length - 1 && proceed) {
      dispatch(setPublishActiveStep(activeStep + 1));
    } else if (!proceed) {
      message.error('Failed to proceed to next step');
    }
    setLoading(false);
  }, [
    activeStep,
    dispatch,
    loadPreview,
    editorRef,
    uploadThumbnail,
    callMutation,
    giideData,
    useTemplate,
    imageMimeTypeLoaded,
  ]);

  const toggleNoIndex = useCallback(async () => {
    setIndexLoading(true);
    const giideDataInput = {
      ...giideData,
      noindex: !giideData.noindex,
    };

    const result = await callMutation(giideDataInput);

    // TODO Why are the types incompatible here? We should update with the result from the server
    if (result.data?.updateGiide) {
      dispatch(updateGiideSettings(giideDataInput));
      setGiideData(giideDataInput);
    } else {
      message.error('Something went wrong updating this setting');
    }

    setIndexLoading(false);
  }, [callMutation, giideData, dispatch, setGiideData]);

  return (
    <Modal
      open={open}
      onCancel={closeModal}
      style={{ top: 60 }}
      footer={
        <Flex gap={'small'} justify={'flex-end'}>
          {activeStep !== 0 ? (
            <Button
              onClick={onClickBack}
              disabled={
                publishing[giideData.id] || previewDetails.loading || loading
              }
            >
              Back
            </Button>
          ) : (
            <Button onClick={closeModal}>Cancel</Button>
          )}
          <Tooltip title={tooltipMessage}>
            {activeStep === stepArray.length - 1 ? (
              <Button
                type={'primary'}
                onClick={saveAndPublish}
                loading={publishing[giideData.id]}
                disabled={
                  publishing[giideData.id] ||
                  !!tooltipMessage ||
                  previewDetails.loading ||
                  !previewDetails.loaded
                }
              >
                Publish
              </Button>
            ) : (
              <Button
                type={'primary'}
                onClick={onClickNext}
                disabled={
                  publishing[giideData.id] ||
                  !!tooltipMessage ||
                  previewDetails.loading ||
                  loading
                }
                loading={loading}
              >
                Next
              </Button>
            )}
          </Tooltip>
        </Flex>
      }
    >
      <Flex vertical={true} gap={'middle'} style={{ paddingBottom: '16px' }}>
        <Steps
          className={styles.giidePublishSteps}
          current={activeStep}
          labelPlacement={'vertical'}
          size="small"
        >
          {stepArray.map(item => (
            <Step key={item} title={StepText[item]} />
          ))}
        </Steps>

        {stepArray[activeStep] === StepKeys.details ? (
          <DetailsSettings
            giideData={giideData}
            updateSpeakerInput={updateSpeakerInput}
            updateSpeakerUrlInput={updateSpeakerUrlInput}
            updateSpeakerName={updateSpeakerName}
            detailsForm={detailsForm}
            progress={uploadAvatarProgress}
            avatar={avatar}
            handleUpload={handleAvatarUpload}
            removeAvatar={removeAvatar}
            publishing={publishing}
            updateGiideInput={updateInput}
            saving={loading}
            updateGiideTitleName={updateGiideTitleName}
            toggleNoIndex={toggleNoIndex}
            noIndexLoading={noIndexLoading}
          />
        ) : stepArray[activeStep] === StepKeys.image ? (
          <ImageSettings
            giideData={giideData}
            updateInput={updateInput}
            updateColor={updateColor}
            updateShade={updateShade}
            updateThumbnail={updateThumbnail}
            updateThumbnailData={updateThumbnailData}
            imageForm={imageForm}
            uploadThumbnail={uploadThumbnail}
            removeThumbnail={removeThumbnail}
            selectCustomThumbnail={selectCustomThumbnail}
            editorRef={editorRef}
            setEditorRef={setEditorRef}
            setLoading={setLoading}
            loading={loading}
            setGiideData={setGiideData}
          />
        ) : stepArray[activeStep] === StepKeys.preview ? (
          previewDetails.loading ? (
            <div className={styles.loadingPreviewWrapper}>
              <LoadingOutlined spin className={styles.loadingPreviewIcon} />
            </div>
          ) : previewDetails.loaded ? (
            <GiidePreview giideId={giideData.id} small={true} />
          ) : (
            <div className={styles.previewLoadFailedTextWrapper}>
              Error occured while preparing your Giide for preview.
            </div>
          )
        ) : null}
      </Flex>
    </Modal>
  );
};

export default PublishFlow;
